<script lang="ts">
	import { Label } from "flowbite-svelte-v1";
	import { getContext } from "svelte";
	import { BundleType } from "#gen/bundles/v1/bundle_pb.js";
	import { ContactDataSchema, SocialLinkSchema, type ContactData, type ContactDataJson } from "#gen/bundles/v1/contact_pb.js";
	import P from "#lib/components/P.svelte";
	import type { OptionType } from "#lib/components/ToggleGroup.svelte";
	import ToggleGroup from "#lib/components/ToggleGroup.svelte";
	import TogglePane from "#lib/components/TogglePane.svelte";
	import SocialDetailsForm from "#lib/components/flow/editor/content/contact/SocialDetailsForm.svelte";
	import type { ComponentEditingProps } from "#lib/components/page/ComponentProps.js";
	import EditorInset from "#lib/components/page/editing/EditorInset.svelte";
	import { create, fromJson, type JsonObject } from "#lib/protobuf-compat.js";
	import { type SuiteStore } from "#lib/stores/suite.js";
	import { listValue, setListValue, setStringValue, setStructValue, stringValue, structValue } from "#lib/struct.js";

	const suite: SuiteStore<ContactData> = getContext("suite");
	const alignmentOptions: OptionType[] = [
		{ name: "Left", value: "justify-start", icon: "format-align-left", iconSet: "mdi" },
		{ name: "Center", value: "justify-center", icon: "format-align-center", iconSet: "mdi" },
		{ name: "Right", value: "justify-end", icon: "format-align-right", iconSet: "mdi" },
	];

	let { id, page, onChange }: ComponentEditingProps = $props();

	const data = $derived(($suite.bundleType === BundleType.CONTACT ? $suite.data?.type.value : getData(structValue($page.config, id) ?? {})) as ContactData);
	const alignment = $derived(stringValue(structValue($page.config, id), "alignment"));

	export function validate(): boolean {
		return true;
	}

	function handleLinkUpdate(contact: ContactDataJson) {
		let config = structValue($page.config, id);
		if (!config) {
			config = {} as JsonObject;
			setStructValue($page.config, id, config);
		}
		setListValue(config, "links", contact.socialLinks ?? []);
		$page = $page;
		onChange?.();
	}

	function handleAlignmentChange(e: CustomEvent<string>) {
		let config = structValue($page.config, id);
		if (!config) {
			config = {} as JsonObject;
			setStructValue($page.config, id, config);
		}
		setStringValue(config, "alignment", e.detail);
		$page = $page;
		onChange?.();
	}

	function getData(config: JsonObject): ContactData {
		const links = listValue(config, "links")?.map(value => fromJson(SocialLinkSchema, value, { ignoreUnknownFields: true })) ?? [];
		return create(ContactDataSchema, { socialLinks: links });
	}
</script>

<EditorInset>
	{#if $suite.bundleType !== BundleType.CONTACT}
		<div class="flex flex-col gap-6 px-6">
			<SocialDetailsForm {data} onUpdate={handleLinkUpdate} />
			<TogglePane enabled={true} label="Appearance">
				{#snippet control()}<div></div>{/snippet}
				<div>
					<Label class="mb-2 grow text-gray-900" for="street2">Alignment</Label>
					<ToggleGroup value={alignment ?? "justify-start"} options={alignmentOptions} on:change={handleAlignmentChange} />
				</div>
			</TogglePane>
		</div>
	{:else}
		<div class="mx-6 rounded-lg border p-6">
			<P weight="semibold" align="center" secondary>This component has no additional configuration options.</P>
			<P size="sm" align="center" secondary>Use the "Content" tab to manage the social links associated with this flow.</P>
		</div>
	{/if}
</EditorInset>
